import { HTMLAttributes, RefObject } from 'react';
import clsx from 'clsx';
import { FieldError } from 'react-hook-form';
import DallasStack from '../../Stack/DallasStack';
import DallasText from '../../Text/DallasText';
import DallasFlex from '../../Stack/DallasFlex';
import ErrorPlacement from '../error-placement';
import ErrorMessageMap from '../error-message-map';

import styles from './DallasFormField.module.scss';

const DEFAULT_ERROR_MESSAGES: ErrorMessageMap = Object.freeze({
  min: 'The value is too low',
  max: 'The value is too high',
  minLength: 'The value is too short',
  maxLength: 'The value is too long',
});

export interface Props extends HTMLAttributes<HTMLElement> {
  className?: string;
  label: string;
  required?: boolean;
  eyebrowEnd?: React.ReactElement;
  addOnStart?: React.ReactElement;
  children?: React.ReactNode;
  addOnEnd?: React.ReactElement;
  error?: FieldError;
  errorPlacement?: ErrorPlacement;
  errorMessages?: ErrorMessageMap;
  footnoteStart?: React.ReactElement;
  footnoteEnd?: React.ReactElement;
  controlContainerRef?: RefObject<HTMLDivElement | null>;
  controlId?: string;
}

export default function DallasFormField({
  controlId,
  controlContainerRef,
  className,
  label,
  required,
  eyebrowEnd: defaultEyebrowEnd,
  onClick,
  addOnStart,
  children,
  addOnEnd,
  error,
  errorPlacement = 'footnote-start',
  errorMessages,
  footnoteStart: defaultFootnoteStart,
  footnoteEnd,
}: Props) {
  const eyebrowStart = label ? (
    <DallasText variant="captionL">
      {label}&nbsp;
      {required && (
        <DallasText variant="captionL" color="error">
          *
        </DallasText>
      )}
    </DallasText>
  ) : null;
  const errorMessage = error && (
    <DallasText role="alert" variant="captionL">
      {error.message ||
        errorMessages?.[error.type] ||
        DEFAULT_ERROR_MESSAGES[error.type]}
    </DallasText>
  );
  const eyebrowEnd =
    errorMessage && errorPlacement === 'eyebrow-end'
      ? errorMessage
      : defaultEyebrowEnd;
  const footnoteStart =
    errorMessage && errorPlacement === 'footnote-start'
      ? errorMessage
      : defaultFootnoteStart;

  return (
    <label className={clsx(className, styles.root)} htmlFor={controlId}>
      {(eyebrowStart || eyebrowEnd) && (
        <DallasStack
          direction="row"
          className={styles.eyebrows}
          gap="md"
          alignHorizontal="space-between"
          alignVertical="end"
        >
          {eyebrowStart}
          <DallasFlex />
          {eyebrowEnd}
        </DallasStack>
      )}
      <div
        ref={controlContainerRef}
        className={styles.controlContainer}
        onClick={onClick}
      >
        {addOnStart && <div className={styles.addOnWrapper}>{addOnStart}</div>}
        {children}
        {addOnEnd && <div className={styles.addOnWrapper}>{addOnEnd}</div>}
      </div>

      {(footnoteStart || footnoteEnd) && (
        <DallasStack direction="row" className={styles.footnotes} gap="md">
          {footnoteStart}
          <DallasFlex />
          {footnoteEnd}
        </DallasStack>
      )}
    </label>
  );
}
